/**
 * SkillCheckboxes - Component for displaying and selecting skills practiced
 */

import {
  Box,
  Button,
  Checkbox,
  Collapsible,
  Flex,
  SimpleGrid,
  Text,
} from "@chakra-ui/react"
import { useState } from "react"
import { FiChevronDown, FiChevronRight } from "react-icons/fi"

import { getSkillLabel } from "@/constants/swimSkills"

interface SkillCheckboxesProps {
  skills: string[]
  selectedSkills: Record<string, boolean>
  onChange: (skills: Record<string, boolean>) => void
  disabled?: boolean
}

export function SkillCheckboxes({
  skills,
  selectedSkills,
  onChange,
  disabled = false,
}: SkillCheckboxesProps) {
  const [isOpen, setIsOpen] = useState(false)

  const selectedCount = Object.values(selectedSkills).filter(Boolean).length

  const handleSkillToggle = (skillKey: string) => {
    onChange({
      ...selectedSkills,
      [skillKey]: !selectedSkills[skillKey],
    })
  }

  const handleSelectAll = () => {
    const allSelected = skills.every((skill) => selectedSkills[skill])
    const newState: Record<string, boolean> = {}
    for (const skill of skills) {
      newState[skill] = !allSelected
    }
    onChange(newState)
  }

  if (skills.length === 0) {
    return null
  }

  return (
    <Collapsible.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
      <Collapsible.Trigger asChild>
        <Button
          variant="ghost"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
          w="full"
          py={2}
          px={3}
          bg="gray.50"
          borderRadius="md"
          fontSize="sm"
          fontWeight="normal"
          color="gray.700"
          h="auto"
          _hover={{ bg: "gray.100" }}
          disabled={disabled}
        >
          {isOpen ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
          <Text fontWeight="medium">Skills Practiced</Text>
          {selectedCount > 0 && (
            <Text color="blue.600" ml="auto">
              {selectedCount} selected
            </Text>
          )}
        </Button>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <Box pt={2} pb={1}>
          <Flex justify="flex-end" mb={2}>
            <Button
              variant="plain"
              size="xs"
              color="blue.600"
              onClick={handleSelectAll}
              disabled={disabled}
            >
              {skills.every((skill) => selectedSkills[skill])
                ? "Deselect All"
                : "Select All"}
            </Button>
          </Flex>

          <SimpleGrid columns={{ base: 1, sm: 2 }} gap={2}>
            {skills.map((skillKey) => (
              <Checkbox.Root
                key={skillKey}
                checked={selectedSkills[skillKey] || false}
                onCheckedChange={() => handleSkillToggle(skillKey)}
                disabled={disabled}
                size="sm"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label fontSize="sm" color="gray.700">
                  {getSkillLabel(skillKey)}
                </Checkbox.Label>
              </Checkbox.Root>
            ))}
          </SimpleGrid>
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}
